import { useState } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Modale } from '../../componenti/base.tsx'
import { CAUSE_VERIFICA } from '../../servizi/dati-mancanti.ts'
import type { SchedaLetta } from '../../servizi/regole-anagrafica.ts'

/**
 * «Da verificare» sulla scheda (460): perche', e come si toglie.
 *
 * Il segno lo mette la migrazione del vecchio archivio quando la data di nascita era impossibile o
 * il codice fiscale mancava. Si toglie da solo quando la causa e' sistemata (la data corretta, il
 * codice fiscale o il motivo per cui manca); chi modifica la scheda lo puo' togliere anche a mano,
 * scrivendo che cosa ha controllato: la motivazione resta nel registro eventi, cifrata.
 */
export function VerificaScheda({ scheda, ricarica }: { scheda: SchedaLetta; ricarica: () => Promise<void> | void }) {
  const [aperta, setAperta] = useState(false)
  if (!scheda.da_verificare) return null
  const cause = scheda.cause_verifica ?? []
  const modifica = scheda.puo.modificare && !scheda.mascherata
  return (
    <>
      <Avviso tipo="attenzione">
        <strong>Scheda da verificare.</strong>{' '}
        {cause.length
          ? <>Viene dal vecchio archivio e {cause.map((c) => CAUSE_VERIFICA[c] ?? c).join('; ')}.</>
          : 'È stata segnata da verificare quando è arrivata dal vecchio archivio.'}
        {' '}Il segno si toglie da solo quando il dato è sistemato
        {modifica ? ', oppure con «Segna come verificata» se i dati sono stati controllati e restano così.' : '.'}
        {modifica && (
          <div className="riga">
            <Bottone variante="secondario" dimensione="piccolo" onClick={() => setAperta(true)}>Segna come verificata</Bottone>
          </div>
        )}
      </Avviso>
      {aperta && <SegnaVerificata scheda={scheda} ricarica={ricarica} chiudi={() => setAperta(false)} />}
    </>
  )
}

function SegnaVerificata({ scheda, ricarica, chiudi }: {
  scheda: SchedaLetta; ricarica: () => Promise<void> | void; chiudi: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function conferma() {
    setErrore(null); setInCorso(true)
    try {
      await api.post(`/persone/${scheda.id}/verificata`, { motivo: motivo.trim() })
      await ricarica()
      chiudi()
    } catch (e) {
      setErrore(e instanceof ErroreApi
        ? (e.stato === 409 ? 'La scheda non è più da verificare.' : e.message)
        : 'Non è andata a buon fine: controlla la connessione e riprova.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo="Segna come verificata" chiudi={chiudi}
            azioni={<>
              <Bottone variante="secondario" onClick={chiudi} disabled={inCorso}>Annulla</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={motivo.trim().length < 5} onClick={() => void conferma()}>
                Segna come verificata
              </Bottone>
            </>}>
      <p>
        La scheda esce dall’elenco delle schede da verificare. I dati che mancano restano da completare:
        questo comando non li inserisce.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <AreaTesto etichetta="Che cosa hai controllato" obbligatorio valore={motivo} cambia={setMotivo} maxLength={500}
                 aiuto="Per esempio: «data di nascita controllata sul passaporto». Resta nel registro eventi." />
    </Modale>
  )
}
